import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const FAQSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'Wie funktioniert das Credit-System?',
      answer: 'Jeder CSV-Export kostet 1 Credit, unabhängig von der Anzahl der enthaltenen Kontakte. Sie können Credits nach Bedarf kaufen, und diese verfallen nie. Neue Nutzer erhalten 10 kostenlose Credits zum Start.'
    },
    {
      question: 'Sind die Kontaktdaten aktuell und korrekt?',
      answer: 'Ja, unsere Datenbank wird kontinuierlich aktualisiert und gepflegt. Wir verwenden die Google Places API und weitere zuverlässige Quellen, um sicherzustellen, dass Sie stets aktuelle und valide Informationen erhalten.'
    },
    {
      question: 'Welche Bundesländer sind verfügbar?',
      answer: 'Aktuell bieten wir vollständige Abdeckung für Rheinland-Pfalz mit über 50.000 Kontakten. Nordrhein-Westfalen folgt in Kürze. Unser Ziel ist es, alle 16 Bundesländer lückenlos abzudecken.'
    },
    {
      question: 'Kann ich die Daten in mein CRM importieren?',
      answer: 'Absolut! Alle Exporte erfolgen im CSV-Format, das von nahezu allen CRM-Systemen (Salesforce, HubSpot, Pipedrive, etc.) unterstützt wird. Sie können die Daten direkt importieren und sofort nutzen.'
    },
    {
      question: 'Ist Adloc DSGVO-konform?',
      answer: 'Ja, alle Daten werden DSGVO-konform erhoben und verarbeitet. Wir verwenden ausschließlich öffentlich zugängliche Informationen und achten streng auf den Datenschutz.'
    },
    {
      question: 'Wie zahle ich für Credits?',
      answer: 'Wir integrieren derzeit PayPal als Zahlungsmethode. Damit können Sie schnell und sicher Credits kaufen. Weitere Zahlungsmethoden folgen in Kürze.'
    },
    {
      question: 'Gibt es versteckte Kosten oder Abonnements?',
      answer: 'Nein, garantiert nicht! Bei Adloc zahlen Sie nur für das, was Sie nutzen. Es gibt keine monatlichen Gebühren, keine Mindestlaufzeiten und keine versteckten Kosten.'
    },
    {
      question: 'Was unterscheidet Adloc von anderen Anbietern?',
      answer: 'Adloc bietet 100% Abdeckung – von Großstädten bis zu kleinen Gemeinden. Andere Anbieter fokussieren sich meist nur auf Ballungsgebiete. Zudem bieten wir faire Preise ohne Abo-Zwang und regelmäßig aktualisierte Daten.'
    }
  ];

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Häufig gestellte Fragen
          </h2>
          <p className="text-lg text-gray-600">
            Haben Sie Fragen? Wir haben Antworten.
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-200 hover:border-[#2835F7] transition-all duration-300"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-100 transition-colors duration-200"
              >
                <span className="text-lg font-bold text-gray-900 pr-8">{faq.question}</span>
                <ChevronDown 
                  className={`w-6 h-6 text-[#2835F7] flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`} 
                />
              </button>
              <div 
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-5">
                  <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-12 text-center bg-gradient-to-br from-[#2835F7] to-[#1a25c9] rounded-2xl p-8 text-white shadow-xl"
        >
          <h3 className="text-2xl font-bold mb-3">Noch Fragen?</h3>
          <p className="text-white/90 mb-6">
            Unser Support-Team hilft Ihnen gerne weiter!
          </p>
          <a 
            href="mailto:kontakt@adloc.io" 
            className="inline-block bg-white text-[#2835F7] px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors duration-200"
          >
            kontakt@adloc.io
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;